/*
 * Replaces the React behaviour that had no Liquid equivalent. Vanilla, no build
 * step — Shopify serves this file as-is.
 *
 *  - Mobile menu        ← Header.tsx useState(isMenuOpen)
 *  - Catalog filters    ← ProductsPage.tsx useState<Filters> + useMemo
 *  - ?model= preselect  ← ContactPage.tsx useSearchParams
 *  - Cart drawer        ← no counterpart; the Vite site had no cart
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- menu -- */

  function initMenu() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    var iconOpen = toggle.querySelector('[data-menu-icon-open]');
    var iconClose = toggle.querySelector('[data-menu-icon-close]');

    toggle.addEventListener('click', function () {
      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      var next = !isOpen;

      toggle.setAttribute('aria-expanded', String(next));
      toggle.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
      nav.classList.toggle('hidden', !next);
      if (iconOpen) iconOpen.classList.toggle('hidden', next);
      if (iconClose) iconClose.classList.toggle('hidden', !next);
    });
  }

  /* ------------------------------------------------------------- filters -- */

  function initCatalogFilters() {
    var root = document.querySelector('[data-catalog]');
    if (!root) return;

    var grid = document.querySelector('[data-catalog-grid]');
    var empty = document.querySelector('[data-catalog-empty]');
    var clear = root.querySelector('[data-filter-clear]');
    var selects = Array.prototype.slice.call(root.querySelectorAll('[data-filter]'));
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-model]'));

    // data-* attribute holding each filter's value on a card.
    var ATTR = {
      capacity: 'capacity',
      bestFor: 'best-for',
      installation: 'installation',
    };

    function apply() {
      var active = {};
      var hasAny = false;

      selects.forEach(function (select) {
        if (select.value) {
          active[select.getAttribute('data-filter')] = select.value;
          hasAny = true;
        }
      });

      var visible = 0;
      cards.forEach(function (card) {
        var match = Object.keys(active).every(function (key) {
          return card.getAttribute('data-' + ATTR[key]) === active[key];
        });
        card.classList.toggle('hidden', !match);
        if (match) visible++;
      });

      if (empty) empty.classList.toggle('hidden', visible !== 0);
      grid.classList.toggle('hidden', visible === 0);
      if (clear) clear.disabled = !hasAny;
    }

    selects.forEach(function (select) {
      select.addEventListener('change', apply);
    });

    if (clear) {
      clear.addEventListener('click', function () {
        selects.forEach(function (select) {
          select.value = '';
        });
        apply();
      });
    }

    apply();
  }

  /* ------------------------------------------------- ?model= preselect --- */

  function initModelPreselect() {
    var params = new URLSearchParams(window.location.search);

    var select = document.querySelector('[data-model-select]');
    var model = params.get('model');
    if (select && model) {
      // Only select a value the dropdown actually offers, mirroring LeadForm.tsx's
      // PRODUCT_OPTIONS.some(...) guard — an unknown ?model= leaves the placeholder.
      var match = Array.prototype.find.call(select.options, function (option) {
        return option.value === model;
      });
      if (match) select.value = match.value;
    }

    // ?cart= carries a cart summary from the cart page's "Request a Quote".
    var summary = params.get('cart');
    var message = document.getElementById('lead-message');
    if (summary && message && !message.value) {
      message.value = 'Requesting a quote for: ' + summary;
    }
  }

  /* ---------------------------------------------------------------- zoom -- */

  // Click-to-zoom on the product image. The page image is sized to the layout,
  // so the modal serves a 2400px copy for the detail on spec and plan graphics.
  function initZoom() {
    var modal = document.querySelector('[data-zoom-modal]');
    if (!modal) return;

    function open() {
      modal.classList.remove('hidden', 'pointer-events-none');
      modal.classList.add('flex');
      document.body.classList.add('overflow-hidden');
      var close = modal.querySelector('button[data-zoom-close]');
      if (close) close.focus();
    }

    function close() {
      modal.classList.add('hidden', 'pointer-events-none');
      modal.classList.remove('flex');
      document.body.classList.remove('overflow-hidden');
    }

    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-zoom-open]')) {
        open();
        return;
      }
      if (event.target.closest('[data-zoom-close]')) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !modal.classList.contains('hidden')) close();
    });
  }

  /* ---------------------------------------------------------------- cart -- */

  function initCart() {
    var drawer = document.querySelector('[data-cart-drawer]');

    function refreshCount(count) {
      var badge = document.querySelector('[data-cart-count]');
      if (!badge) return;
      badge.textContent = count;
      badge.classList.toggle('hidden', count === 0);
    }

    function openDrawer() {
      if (!drawer) return;
      var overlay = drawer.querySelector('[data-cart-overlay]');
      var panel = drawer.querySelector('[data-cart-panel]');

      drawer.classList.remove('hidden');
      // Removing the class beats adding pointer-events-auto: with both classes
      // present, whichever utility comes later in the compiled CSS wins.
      drawer.classList.remove('pointer-events-none');
      // Next frame, so the transition has a start state to animate from.
      requestAnimationFrame(function () {
        if (overlay) overlay.classList.replace('opacity-0', 'opacity-100');
        if (panel) panel.classList.remove('translate-x-full');
      });
      document.body.classList.add('overflow-hidden');
    }

    function closeDrawer() {
      if (!drawer) return;
      var overlay = drawer.querySelector('[data-cart-overlay]');
      var panel = drawer.querySelector('[data-cart-panel]');

      if (overlay) overlay.classList.replace('opacity-100', 'opacity-0');
      if (panel) panel.classList.add('translate-x-full');
      document.body.classList.remove('overflow-hidden');
      window.setTimeout(function () {
        drawer.classList.add('hidden');
        drawer.classList.add('pointer-events-none');
      }, 200);
    }

    function rerender() {
      return fetch('/?sections=cart-drawer-items')
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          var body = document.querySelector('[data-cart-body]');
          if (body && data['cart-drawer-items']) {
            body.innerHTML = data['cart-drawer-items'];
          }
          return fetch('/cart.js').then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          refreshCount(cart.item_count);
        });
    }

    function mutate(body) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(rerender);
    }

    // Discount codes. /discount/{code} is the only storefront route that puts a
    // code on the cart session before checkout; once it is there, the cart
    // renders the markdown through cart.discount_applications. The drawer can
    // re-render in place, the cart page has to reload to pick up the new totals.
    function applyDiscount(scope) {
      var input = scope.querySelector('[data-discount-input]');
      var error = scope.querySelector('[data-discount-error]');
      var button = scope.querySelector('[data-discount-apply]');
      var code = input ? input.value.trim() : '';
      if (!code) return;

      function fail() {
        if (!error) return;
        error.textContent = error.getAttribute('data-discount-error');
        error.classList.remove('hidden');
      }

      if (error) error.classList.add('hidden');
      if (button) button.disabled = true;

      fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js')
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {
          var codes = cart.discount_codes || [];
          var accepted = codes.some(function (entry) {
            return (
              entry.code &&
              entry.code.toLowerCase() === code.toLowerCase() &&
              entry.applicable !== false
            );
          });

          if (!accepted) {
            fail();
            return;
          }

          if (input) input.value = '';
          if (drawer && !drawer.classList.contains('hidden')) return rerender();
          window.location.reload();
        })
        .catch(function () {
          fail();
        })
        .finally(function () {
          if (button) button.disabled = false;
        });
    }

    // Add to cart — intercept the product form so the drawer opens in place.
    var form = document.getElementById('product-form');
    if (form && drawer) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var button = form.querySelector('[data-add-to-cart]');
        if (button) button.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            return rerender();
          })
          .then(openDrawer)
          .catch(function () {
            // Let Shopify's own /cart page report the problem rather than
            // swallowing it — the customer still gets their item.
            form.submit();
          })
          .finally(function () {
            if (button) button.disabled = false;
          });
      });
    }

    document.addEventListener('click', function (event) {
      var openBtn = event.target.closest('[data-cart-open]');
      if (openBtn && drawer) {
        event.preventDefault();
        openDrawer();
        return;
      }

      if (event.target.closest('[data-cart-close]') || event.target.closest('[data-cart-overlay]')) {
        closeDrawer();
        return;
      }

      var remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        mutate({ id: remove.getAttribute('data-line-key'), quantity: 0 });
        return;
      }

      var apply = event.target.closest('[data-discount-apply]');
      if (apply) {
        applyDiscount(apply.closest('[data-discount-form]'));
        return;
      }

      // Suggested add-on. The drawer refreshes in place; the cart page has no
      // live section to swap, so it reloads.
      var addon = event.target.closest('[data-addon-add]');
      if (addon) {
        addon.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: addon.getAttribute('data-variant-id'), quantity: 1 }),
        })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            if (drawer && !drawer.classList.contains('hidden')) return rerender();
            window.location.reload();
          })
          .catch(function () {
            addon.disabled = false;
          });
      }
    });

    document.addEventListener('change', function (event) {
      var qty = event.target.closest('[data-cart-qty]');
      if (!qty) return;
      mutate({ id: qty.getAttribute('data-line-key'), quantity: parseInt(qty.value, 10) });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer && !drawer.classList.contains('hidden')) {
        closeDrawer();
        return;
      }

      // The discount field is a div, not a form, so Enter has to be wired up.
      if (event.key === 'Enter' && event.target.closest('[data-discount-input]')) {
        event.preventDefault();
        applyDiscount(event.target.closest('[data-discount-form]'));
      }
    });
  }

  /* ---------------------------------------------------------------- init -- */

  function init() {
    initMenu();
    initCatalogFilters();
    initModelPreselect();
    initZoom();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor tears down and re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
